<template>
  <div class="max-w-4xl mx-auto p-6 my-40">
    <h2 class="text-5xl font-bold mb-6 text-center text-gray-900">
      On répond à vos questions ❓
    </h2>

    <!-- Sous-titre -->
    <p class="text-gray-600 text-center mb-8">
      Toutes les réponses à vos questions pour créer et gérer
      votre site vitrine pro.
    </p>

    <div v-for="(item, index) in faq" :key="index"
      class="border border-gray-200 rounded-2xl shadow-sm hover:shadow-md transition-all duration-300 bg-white mb-4">
      <!-- Question (bouton) -->
      <button class="w-full flex justify-between items-center px-6 py-5 text-left focus:outline-none group"
        @click="toggle(index)">
        <div class="flex items-center gap-4">
          <!-- Numéro stylé -->
          <span
            class="flex items-center justify-center w-8 h-8 rounded-full bg-primary text-white font-bold shadow-md text-sm">
            {{ index + 1 }}
          </span>

          <!-- Texte question -->
          <span class="font-semibold text-lg text-gray-800 group-hover:text-primary transition-colors duration-300">
            {{ item.question }}
          </span>
        </div>

        <!-- Flèche -->
        <svg :class="{ 'transform rotate-180': openIndex === index }"
          class="w-6 h-6 text-gray-400 group-hover:text-primary transition-transform duration-300" fill="none"
          stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"></path>
        </svg>
      </button>

      <!-- Réponse (contenu accordéon) -->
      <transition name="accordion">
        <div v-show="openIndex === index" class="px-6 pb-6 text-gray-600 leading-relaxed text-base bg-white">
          <p v-html="item.answer"></p>
        </div>
      </transition>
    </div>
  </div>
</template>

<script setup>
import { ref } from 'vue'

const openIndex = ref(null)

const toggle = (index) => {
  openIndex.value = openIndex.value === index ? null : index
}

const faq = [
  {
    question: 'Combien coûte MyProfolia ?',
    answer: `💰 L’abonnement MyProfolia est simple et transparent :<br/><br/>
• <strong>5,99 € / mois</strong> – sans engagement<br/>
• <strong>59,99 € / an</strong> – avec engagement de 12 mois (🌟 soit 20 % d’économie)<br/><br/>
Tout est inclus : création de votre site vitrine, hébergement sécurisé, gestion des contenus et statistiques de visites.<br/><br/>
✅ <em>Aucun frais caché</em> – vous pouvez résilier à tout moment depuis votre espace client.`
  },
  {
    question: 'Comment créer ma page vitrine ?',
    answer: `⚡ C’est rapide et accessible à tous, même sans compétence technique :<br/><br/>
1. Créez votre compte<br/>
2. Remplissez vos infos (coordonnées, prestations, tarifs, photos…)<br/>
3. Prévisualisez le rendu<br/>
4. Publiez votre site en choisissant votre mode de paiement<br/><br/>
✅ Votre vitrine est en ligne immédiatement et vous pouvez la partager sur vos réseaux sociaux, par mail ou sur vos cartes de visite.<br/><br/>
➔ Modifiable gratuitement et à tout moment depuis votre espace admin.`
  },
  {
    question: 'Que comprend l’abonnement ?',
    answer: `Avec MyProfolia, vous bénéficiez de :<br/><br/>
🌐 Une page vitrine personnalisée avec vos infos, photos et réseaux sociaux<br/><br/>
🔒 Un hébergement sécurisé 24h/24 et fiable<br/><br/>
🛠️ Un tableau de bord simple pour modifier vos contenus à tout moment<br/><br/>
💬 Un support client réactif qui vous accompagne<br/><br/>
🔗 Une URL personnalisée : www.myprofolia.com/votre-nom<br/><br/>
📱 Un site responsive (ordinateur, tablette, smartphone)`
  },
  {
    question: 'Y a-t-il des frais cachés ?',
    answer: `❌ Non, aucun frais caché. Tout est inclus dans l’abonnement mensuel ou annuel.`
  },
  {
    question: 'Combien de temps pour mettre ma page en ligne ?',
    answer: `⏱️ Seulement 5 minutes si vos photos et textes sont prêts.<br/><br/>
🚀 Votre page est publiée immédiatement après validation du paiement.`
  },
  {
    question: 'Puis-je modifier ma page après sa création ?',
    answer: `✅ Oui, autant de fois que vous voulez !<br/><br/>
Vous pouvez mettre à jour vos textes, photos, tarifs et horaires gratuitement et en temps réel.`
  },
  {
    question: 'Est-ce que ma page sera visible sur Google ?',
    answer: `🚀 Oui ! Chaque page est optimisée SEO pour améliorer votre visibilité et attirer plus de clients.`
  },
  {
    question: 'Le paiement est-il sécurisé ?',
    answer: `🔒 Absolument. Nous utilisons Stripe, une solution de paiement reconnue et sécurisée dans le monde entier.<br/><br/>
💳 Moyens de paiement acceptés : <strong>Visa, Mastercard </strong>.`
  },
  {
    question: 'Comment résilier mon abonnement ?',
    answer: `👆 Rien de plus simple : vous pouvez résilier en 1 clic depuis votre espace client.<br/><br/>
✅ La résiliation prend effet à la fin de la période déjà payée, sans frais supplémentaires.`
  },
  {
    question: 'Que faire si j’ai besoin d’aide ?',
    answer: `🙋‍♂️ Notre équipe est disponible pour vous accompagner à tout moment.<br/><br/>
• Via le formulaire de contact sur le site<br/>
• Par email : <a href="mailto:contact@myprofolia.com" class="text-blue-600 underline">contact@myprofolia.com</a><br/><br/>
📩 Nous répondons rapidement pour vous aider à tirer le meilleur de MyProfolia.`
  },
  {
    question: "Pourquoi je n'arrive pas à prévisualiser ma page ?",
    answer: `
⚠️ La prévisualisation peut ne pas s’ouvrir si votre navigateur bloque les pop-ups.<br/><br/>

<strong class="text-lg text-primary">📱 iPhone et iPad</strong><br/>
<ul class="list-disc list-inside mt-2">
<strong>SUR SAFARI :</strong> <br/>
- Accédez à "Réglages" de l'Iphone ou de l'Ipad > Apps > Safari. <br/>
- Désactivez l’option Bloquer les pop-ups. <br/>
<strong>SUR CHROME :</strong> <br/> 
- Ouvrez l’application Chrome. <br/>
- Appuyez sur le menu avec les trois points (⋮) → Paramètres → Paramètres du contenu. <br/>
- Désactivez "Bloquer les pop-ups".
</ul>

<br/><strong class="text-lg text-primary">🤖 Android</strong><br/>
<ul class="list-disc list-inside mt-2">
<strong> SUR CHROME :</strong> <br/>
- Ouvrez l’application Chrome sur votre téléphone ou tablette. <br/>
- Appuyez sur le menu avec les trois points (⋮) → Paramètres → Paramètres du site → Pop-ups et redirections. <br/>
- Activez "Autoriser les pop-ups et redirections".<br/>
<strong> SUR FIREFOX :</strong> <br/>
- Ouvrez l’application Firefox. <br/>
- Appuyez sur le menu avec les trois points (⋮) → Paramètres → Paramètres du site <br/>
- Désactivez "Bloquer les pop-ups". <br/>

<br/><strong class="text-lg text-primary">💻 Ordinateur (Mac / Windows)</strong><br/>
<ul class="list-disc list-inside mt-2">
<strong>SUR SAFARI (Mac) :</strong><br/>
- Ouvrez le navigateur Safari.<br/>
- Sur la barre d'outils, appuyez sur Safari → Préférences → Sites web → Fenêtres pop-up <br/>
- Choisissez <em>Autoriser</em> pour 
<a href="https://www.myprofolia.com" class="text-blue-600 underline">https://www.myprofolia.com</a>.<br/>
<strong>SUR CHROME :</strong> <br/>
- Ouvrez le navigateur Chrome. <br/>
- Appuyez sur le menu avec les trois points (⋮) → Paramètres → Confidentialité et sécurité → Paramètres des sites → Pop-ups et redirections. <br/>
- Cochez "Les sites peuvent afficher des pop-ups et utiliser des redirections".<br/>
<strong>SUR FIREFOX :</strong> <br/>
- Ouvrez le navigateur Firefox. <br/>
- Appuyez sur le menu avec les trois points (⋮) → Options → Vie privée et sécurité → Permissions → Bloquer les fenêtres pop-up.<br/>
- Cliquez sur <em>Exceptions…</em> et ajoutez 
<a href="https://www.myprofolia.com" class="text-blue-600 underline">https://www.myprofolia.com</a>.
</ul>

<br/>✅ Après avoir désactivé le blocage des pop-ups, la prévisualisation fonctionnera normalement.
`
  }

]
</script>